import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
// components
import { PageHeader } from "../components/PageSpecificComponents";
import { Card, SideModal } from "../components/UI";
import TiketDetails from "../components/TiketDetails/TiketDetails";
import Chat from "../containers/chat/Chat";
import ListHeader from "../components/PageSpecificComponents/pagesComponents/dashboard/list-header/ListHeader";
import DashboardServiceItem from "../components/lists/dashboard/service-item";
// import NotificationItem from "../components/lists/dashboard/notification-item";
import Plan from "../components/PageSpecificComponents/pagesComponents/dashboard/plan/Plan";
import Onboarding from "../components/PageSpecificComponents/pagesComponents/dashboard/onboarding/Onboarding";
import { useOnboardingStatus } from "../components/PageSpecificComponents/pagesComponents/dashboard/onboarding/useOnboardingStatus";

// Icons
import { Shield } from "../SVGS";

// Domain Model
// import { Service } from "../models/app/domain";

// Calls
import { fetchDashboardRequest } from "../api/calls/dashboard.call";
import { fetchServiceDetailsRequest } from "../api/calls/service-requests.call";

// Constants
import * as modalTypes from "../constants/serviceRequests.constants";
import * as businessProfileConsts from "../constants/business-profile/business-profile.constants";
import { BUSINESS_PROFILE } from "../constants/menu-items/menu-items.constants";

// Actions
// import { seeNewMessages } from "../redux/actions/services.actions";
import { setNewChatMessage } from "../redux/actions/chat.actions";
import { fetchOnboardingStatus } from "../redux/actions/app.actions";

// Utils
import { enableScroll, disableScroll, isEmpty } from "../utils";
import { convertTimeStampToDate } from "../utils/timeStampToDate";

// styles
import { DashboardStyles } from "../pagesStyles/dashboard.styles";

// Placeholder shown in the two dashboard data cards until the branch is live.
const LockedCard = ({ title }) => (
  <>
    <div className="lockedHeader">
      <p className="lockedNumber">—</p>
      <p className="lockedTitle">{title}</p>
    </div>
    <div className="lockedBody">
      <span className="lockedIcon">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
          <rect
            x="5"
            y="10.5"
            width="14"
            height="9.5"
            rx="2.2"
            stroke="currentColor"
            strokeWidth="1.8"
          />
          <path
            d="M8 10.5V8a4 4 0 018 0v2.5"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
          />
        </svg>
      </span>
      <p className="lockedText">
        Unlocks once your branch is live and can receive jobs.
      </p>
    </div>
  </>
);

const Dashboard = () => {
  const { activeBranch, user, dashboardData, onboardingStatus, menuItems } =
    useSelector(state => state.app);
  const { newMessage } = useSelector(state => state.chatReducer);
  const [dashboardState, setDashboardState] = useState({
    services: [],
    totalServices: 0,
    requests: [],
    totalRequests: 0,
    notifications: [],
    totalNotifications: 0,
    modalType: undefined,
    selectedService: undefined
  });
  const [isChatOpen, setIsChatOpen] = useState(false);
  const dispatch = useDispatch();

  const onboarding = useOnboardingStatus();
  // Only engage onboarding once the authoritative status has loaded from
  // /branches/onboarding-status, so established users don't see a flash of the
  // checklist / locked cards before it resolves.
  const statusLoaded = !!onboardingStatus || onboarding.isDevOverride;
  const isLive = !statusLoaded || onboarding.allRequiredDone;

  const initials = `${user?.firstName?.[0] || ""}${
    user?.lastName?.[0] || ""
  }`.toUpperCase();
  const profileImageUrl = user?.profileImage?.url;
  // "Owner or Manager-with-business-access" — the same capability gate the
  // sidebar and router use for the whole business/billing/subscription area.
  const canAccessBusiness = menuItems.some(item => item.key === BUSINESS_PROFILE);
  const activePlan =
    canAccessBusiness &&
    isLive &&
    dashboardData?.verificationStatus ===
      businessProfileConsts.BUSINESS_STATUS_VERIFIED &&
    !isEmpty(dashboardData?.subscriptionPlanName);

  const getDashboardData = async () => {
    const response = await fetchDashboardRequest();
    if (response.hasError) {
      return;
    }

    setDashboardState(prevState => ({
      ...prevState,
      services: response.services.items,
      totalServices: response.services.total,
      requests: response.requests.items,
      totalRequests: response.requests.total,
      notifications: response.notifications.items,
      totalNotifications: response.notifications.total
    }));
  };

  const closeModal = () => {
    setDashboardState(prevState => ({
      ...prevState,
      modalType: undefined,
      selectedService: undefined
    }));
    getDashboardData();
  };

  const serviceDetails = async serviceId => {
    const response = await fetchServiceDetailsRequest(serviceId);
    if (response.hasError) {
      return;
    }
    setDashboardState(prevState => ({
      ...prevState,
      modalType: modalTypes.TIKET_DETAILS,
      selectedService: response.service
    }));
    disableScroll();
  };

  useEffect(() => {
    getDashboardData();
    // Refresh the authoritative onboarding status on entry and whenever the
    // active branch changes, so the checklist (and the verify/plan chip, which
    // the reducer refreshes from the same payload) reflect real progress.
    dispatch(fetchOnboardingStatus());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeBranch]);

  useEffect(() => {
    if (newMessage && !isChatOpen) {
      setDashboardState(prevState => {
        if (!prevState.selectedService) return prevState;
        return {
          ...prevState,
          selectedService: {
            ...prevState.selectedService,
            newMessagesCount:
              (prevState.selectedService.newMessagesCount || 0) + 1
          }
        };
      });
      dispatch(setNewChatMessage(undefined));
    }
  }, [newMessage, isChatOpen, dispatch]);

  const toggleChat = () => {
    setIsChatOpen(prevValue => !prevValue);
    getDashboardData();
    setDashboardState(prevState => ({
      ...prevState,
      selectedService: { ...prevState.selectedService, newMessagesCount: 0 }
    }));
  };

  // const openNotification = async serviceId => {
  //   const response = await fetchServiceDetailsRequest(serviceId);
  //   if (response.hasError) {
  //     return;
  //   }
  //   setDashboardState(prevState => ({
  //     ...prevState,
  //     modalType: modalTypes.TIKET_DETAILS,
  //     selectedService: response.service
  //   }));
  //   setIsChatOpen(prevValue => !prevValue);
  // };

  const generateDay = () => {
    const day = new Date().getDay();
    switch (day) {
      case 1:
        return "Monday";
      case 2:
        return "Tuesday";
      case 3:
        return "Wednesday";
      case 4:
        return "Thursday";
      case 5:
        return "Friday";
      case 6:
        return "Saturday";
      case 0:
        return "Sunday";
      default:
        return "Unknown";
    }
  };

  return (
    <DashboardStyles>
      <PageHeader title="Dashboard" />
      <div className="userContainer">
        <div className="slimBar">
          <div className="avatar">
            {profileImageUrl ? (
              <img src={profileImageUrl} alt="profile" />
            ) : (
              initials || "U"
            )}
          </div>
          <div className="info">
            <div className="eyebrow">Welcome back</div>
            <div className="name">
              {user?.firstName} {user?.lastName}
            </div>
            <div className="tagline">
              <span className="rolePill">
                <Shield stroke="#FFFFFF" /> {user?.roles?.[0]?.name}
              </span>
            </div>
          </div>
          <div className="barRight">
            <div className="dateBlock">
              <div className="day">{generateDay()}</div>
              <div className="date">
                {convertTimeStampToDate(new Date().getTime() / 1000)}
              </div>
            </div>
            {activePlan && (
              <Plan
                subscriptionPlanName={dashboardData.subscriptionPlanName}
                subscriptionPlanEndPeriod={
                  dashboardData.currentSubscription.endPeriod
                }
              />
            )}
          </div>
        </div>
      </div>
      <div className="onboardingContainer">
        {statusLoaded && <Onboarding status={onboarding} />}
      </div>
      <div className="cardsContainer">
        <Card className="card">
          {isLive ? (
            <>
              <ListHeader
                number={dashboardState.totalRequests}
                title="New Service Requests"
                numberColor="#0093EC"
                link="/service-requests"
              />
              {!isEmpty(dashboardState.requests) && (
                <>
                  <div className="servicesList">
                    {dashboardState.requests.map(request => (
                      <DashboardServiceItem
                        {...request}
                        key={request.id}
                        onClick={() => serviceDetails(request.id)}
                      />
                    ))}
                  </div>
                  {dashboardState.totalRequests > 4 && (
                    <p className="hasMore">
                      + {dashboardState.totalRequests - 4} more
                    </p>
                  )}
                </>
              )}
            </>
          ) : (
            <LockedCard title="New Service Requests" />
          )}
        </Card>
        <Card className="card">
          {isLive ? (
            <>
              <ListHeader
                number={dashboardState.totalServices}
                title="Services for today"
                numberColor="#03CA82"
                link="/services"
              />
              {!isEmpty(dashboardState.services) && (
                <>
                  <div className="servicesList">
                    {dashboardState.services.map(service => (
                      <DashboardServiceItem
                        {...service}
                        key={service.id}
                        onClick={() => serviceDetails(service.id)}
                      />
                    ))}
                  </div>
                  {dashboardState.totalServices > 4 && (
                    <p className="hasMore">
                      + {dashboardState.totalServices - 4} more
                    </p>
                  )}
                </>
              )}
            </>
          ) : (
            <LockedCard title="Services for today" />
          )}
        </Card>
        {/* <Card className="card">
          <ListHeader
            number={dashboardState.totalNotifications}
            title="New notifications"
            numberColor="#FF4747"
          />
          {!isEmpty(dashboardState.totalNotifications) && (
            <>
              <div className="servicesList">
                {dashboardState.notifications.map(notification => (
                  <DashboardServiceItem
                    {...notification}
                    bgColor={"#EEF7FC"}
                    key={notification.id}
                    onClick={() => openNotification(notification.id)}
                    isNotification
                  />
                ))}
              </div>
              {dashboardState.totalNotifications > 4 && (
                <p className="hasMore">
                  + {dashboardState.totalNotifications - 4} more
                </p>
              )}
            </>
          )}
        </Card> */}
        {dashboardState.modalType === modalTypes.TIKET_DETAILS && (
          <SideModal
            isChatOpen={isChatOpen}
            closeModal={() => {
              enableScroll();
              closeModal();
              setIsChatOpen(false);
            }}
          >
            <TiketDetails
              serviceDetails={dashboardState.selectedService}
              // tiketDetailsModalType={tiketDetailsModalType}
              toggleChat={() => toggleChat(dashboardState.selectedService.id)}
              closeModal={() => {
                enableScroll();
                closeModal();
                setIsChatOpen(false);
              }}
            />
            {dashboardState.selectedService && (
              <Chat
                service={dashboardState.selectedService}
                isOpen={isChatOpen}
                onClose={() => setIsChatOpen(false)}
              />
            )}
          </SideModal>
        )}
      </div>
    </DashboardStyles>
  );
};

export default Dashboard;
